import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function WordJumblesExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= NORMALIZED QUESTIONS ================= */
  const questions = Array.isArray(exercise.wordJumblesQuestions)
    ? exercise.wordJumblesQuestions.map((q) => ({
        type: "single",
        jumbled: q.jumbled || "",
        answer: Array.isArray(q.word) ? q.word[0] : "", // engine needs single answer
        answers: q.word || [],
        requireCapitalization: q.requireCapitalization,
        skill: "vocabulary",
      }))
    : [];

  /* ================= ENGINE ================= */
  const {
    answers,
    setAnswer,
    submitIfReady,
    getInputState,
    reset,
  } = useExerciseEngine({
    questions,
    showResult: showResults,
    onComplete,
    externalAnswers: userAnswer || {},
  });

 

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("wordJumblesQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    const next = [...exercise.wordJumblesQuestions];
    next[qIdx][field] = value;
    updateQuestions(next);
  };

  const handleWordArrayChange = (qIdx, value) => {
    const next = [...exercise.wordJumblesQuestions];
    next[qIdx].word = value.split(",").map((v) => v.trim());
    updateQuestions(next);
  };

  if (isEditMode) {
    return (
      <div className="space-y-8">
        {exercise.wordJumblesQuestions?.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Jumbled Word ${qIdx + 1}`}
              value={q.jumbled}
              onChange={(v) =>
                handleQuestionChange(qIdx, "jumbled", v)
              }
            />

            <StringField
              label="Correct Answer(s)"
              value={(q.word || []).join(", ")}
              onChange={(v) =>
                handleWordArrayChange(qIdx, v)
              }
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                handleQuestionChange(
                  qIdx,
                  "requireCapitalization",
                  c
                )
              }
            />

            <Button
              variant="destructive"
              onClick={() =>
                updateQuestions(
                  exercise.wordJumblesQuestions.filter(
                    (_, i) => i !== qIdx
                  )
                )
              }
            >
              Remove
            </Button>
          </Card>
        ))}

        <Button
          onClick={() =>
            updateQuestions([
              ...(exercise.wordJumblesQuestions || []),
              { jumbled: "", word: [] },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Word Jumbles</CardTitle>
        <CardDescription>Unscramble the word</CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const userValue = answers[qIdx] || "";

          const state = getInputState(userValue, q.answer);

          return (
            <div
              key={qIdx}
              className="p-10 rounded-2xl text-center space-y-6 bg-muted"
            >
              <div className="text-5xl font-bold tracking-widest text-blue-700">
                {q.jumbled}
              </div>

              <Input
                value={userValue}
                disabled={showResults}
                onChange={(e) => {
                  setAnswer(qIdx, e.target.value);
                  submitIfReady();
                }}
                className={cn(
                  "max-w-md mx-auto text-2xl text-center h-14 font-bold",
                  state.className
                )}
              />

              {showResults && userValue && (
                <p
                  className={cn(
                    "text-sm font-medium",
                    state.isCorrect
                      ? "text-green-600"
                      : "text-red-600"
                  )}
                >
                  {state.isCorrect ? "Correct" : "Wrong"}
                </p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}